import { useState, useEffect, useMemo, useRef, type DragEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ApplicationListLoadingState } from "../components/ApplicationListLoadingState";
import { ApplicationFilters } from "../components/ApplicationFilters";
import { ApplicationTable } from "../components/ApplicationTable";
import { ApplicationGrid } from "../components/ApplicationGrid";
import { ProductDetailDrawer } from "../components/ProductDetailDrawer";
import { useIsMobile } from "@/hooks/useIsMobile";
import { PageHeader } from "../../invitee/components/PageHeader";
import { DenialReasonsModal } from "../components/DenialReasonsModal";
import { ConfirmationModal } from "../components/modals/ConfirmationModal";
import { useApplicationList } from "../hooks/useApplicationList";
import {
  LIBERTY_ROLES_FOR_APPLICATION_LIST,
  PER_PAGE_OPTIONS,
} from "../utils/applicationConstants";
import { impersonateUser } from "../services/applicationService";
import { API_ENDPOINTS } from "@/lib/api/config";
import apiClient from "@/lib/api/apiClient";
import { setAuthToken } from "@/lib/api/apiClient";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { selectUser, updateUser } from "@/features/auth/authSlice";
import { STORAGE_KEYS } from "@/constants/storageKeys";
import styles from "../styles/applicationList.module.css";
import { showError } from "@/lib/utils/notifications";
import { ListPaginationControls } from "@/components/ui/ListPaginationBar";
import { StickyTableScrollbar } from "@/components/ui/StickyTableScrollbar";
import { GotoPageInput } from "../components/GotoPageInput";
import { getCurrentPathForImpersonateReturn } from "@/lib/utils/impersonateReturnPath";
import { getMobileImpersonationUrl } from "../utils/mobileImpersonation";
import { getProductLongDescription } from "../utils/applicationFormatters";
import { hasPolicy } from "../utils/applicationPolicies";
import {
  APPLICATION_LIST_COLUMN_OPTIONS,
  APPLICATION_LIST_CUSTOMIZABLE_COLUMN_OPTIONS,
  APPLICATION_LIST_LOCKED_COLUMN_IDS,
  isApplicationListLockedColumn,
  withLockedApplicationListColumns,
  type ApplicationListColumnId,
} from "../utils/applicationListColumns";
import type { Application } from "../types/application";
import type { ApplicationColumnSettings } from '../types/pages'
import { APPLICATION_COLUMN_VISIBILITY_STORAGE_KEY } from '../constants'

const DEFAULT_APPLICATION_COLUMN_IDS = APPLICATION_LIST_COLUMN_OPTIONS.map(
  (column) => column.id,
);
const DEFAULT_CUSTOMIZABLE_COLUMN_IDS = APPLICATION_LIST_CUSTOMIZABLE_COLUMN_OPTIONS.map(
  (column) => column.id,
);

const normalizeColumnIds = (columnIds: unknown): ApplicationListColumnId[] => {
  if (!Array.isArray(columnIds)) return DEFAULT_APPLICATION_COLUMN_IDS;

  const knownColumnIds = new Set(DEFAULT_APPLICATION_COLUMN_IDS);
  const parsedColumnIds = columnIds.filter(
    (columnId): columnId is ApplicationListColumnId =>
      typeof columnId === "string" &&
      knownColumnIds.has(columnId as ApplicationListColumnId),
  );

  return withLockedApplicationListColumns([
    ...parsedColumnIds.filter((columnId) => !isApplicationListLockedColumn(columnId)),
    ...DEFAULT_APPLICATION_COLUMN_IDS.filter(
      (columnId) =>
        !parsedColumnIds.includes(columnId) &&
        !isApplicationListLockedColumn(columnId),
    ),
  ]);
};

function loadApplicationColumnSettings(): ApplicationColumnSettings {
  const defaultSettings = {
    visibleColumnIds: DEFAULT_APPLICATION_COLUMN_IDS,
    columnOrderIds: DEFAULT_APPLICATION_COLUMN_IDS,
  };

  if (typeof window === "undefined") return defaultSettings;

  try {
    const storedValue = window.localStorage.getItem(
      APPLICATION_COLUMN_VISIBILITY_STORAGE_KEY,
    );
    if (!storedValue) return defaultSettings;

    const parsedValue = JSON.parse(storedValue);
    if (Array.isArray(parsedValue)) {
      // Old format without newer columns → reinit to current defaults.
      if (!parsedValue.includes("income_score") || !parsedValue.includes("screening_approach")) return defaultSettings;

      return {
      visibleColumnIds: withLockedApplicationListColumns(
        DEFAULT_APPLICATION_COLUMN_IDS.filter((columnId) =>
          parsedValue.includes(columnId),
        ),
      ),
      columnOrderIds: DEFAULT_APPLICATION_COLUMN_IDS,
    };
    }

    if (!parsedValue || typeof parsedValue !== "object") return defaultSettings;

    const parsedSettings = parsedValue as Partial<ApplicationColumnSettings>;

    // Saved prefs from before newer columns → reinit defaults.
    // Once present in saved order, keep the user's visibility/order choices.
    if (
      !parsedSettings.columnOrderIds?.includes("income_score") ||
      !parsedSettings.columnOrderIds?.includes("screening_approach")
    ) {
      return defaultSettings;
    }

    return {
      visibleColumnIds: withLockedApplicationListColumns(
        DEFAULT_APPLICATION_COLUMN_IDS.filter((columnId) =>
          parsedSettings.visibleColumnIds?.includes(columnId),
        ),
      ),
      columnOrderIds: normalizeColumnIds(parsedSettings.columnOrderIds),
    };
  } catch {
    return defaultSettings;
  }
}

export function ApplicationList() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const {
    applications,
    loading,
    sorting,
    error,
    pagination,
    filters,
    applyFilters,
    resetFilters,
    changePage,
    changePerPage,
    sort,
  } = useApplicationList();

  const [denialModalOpen, setDenialModalOpen] = useState(false);
  const [selectedApplicantId, setSelectedApplicantId] = useState<number | null>(
    null,
  );
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [pendingImpersonate, setPendingImpersonate] = useState<{
    roleId: number;
    applicantId: number;
    isMobile?: boolean;
  } | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isColumnsOpen, setIsColumnsOpen] = useState(false);
  const [columnSearchTerm, setColumnSearchTerm] = useState("");
  const [draggedColumnId, setDraggedColumnId] =
    useState<ApplicationListColumnId | null>(null);
  const [dragPreviewColumnOrderIds, setDragPreviewColumnOrderIds] =
    useState<ApplicationListColumnId[] | null>(null);
  const [columnSettings, setColumnSettings] = useState<ApplicationColumnSettings>(
    loadApplicationColumnSettings,
  );
  const { visibleColumnIds, columnOrderIds } = columnSettings;
  const dropdownColumnOrderIds = dragPreviewColumnOrderIds ?? columnOrderIds;
  const columnsMenuRef = useRef<HTMLDivElement | null>(null);
  const columnsListRef = useRef<HTMLDivElement | null>(null);
  const resultsScrollRef = useRef<HTMLDivElement | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Application | null>(null);

  const isMobile = useIsMobile();
  const isListView = !isMobile;

  const filterSearchValue = filters.search || "";
  const [searchDraft, setSearchDraft] = useState({
    source: filterSearchValue,
    value: filterSearchValue,
  });
  const searchTerm =
    searchDraft.source === filterSearchValue ? searchDraft.value : filterSearchValue;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== filterSearchValue) {
        applyFilters({ search: searchTerm });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, filterSearchValue, applyFilters]);

  useEffect(() => {
    window.localStorage.setItem(
      APPLICATION_COLUMN_VISIBILITY_STORAGE_KEY,
      JSON.stringify(columnSettings),
    );
  }, [columnSettings]);

  useEffect(() => {
    if (!isColumnsOpen) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (
        columnsMenuRef.current &&
        !columnsMenuRef.current.contains(event.target as Node)
      ) {
        setIsColumnsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsColumnsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isColumnsOpen]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search && String(filters.search).trim()) count++;
    if (filters.applicantStatus && filters.applicantStatus.length > 0) count++;
    if (filters.progress_status && filters.progress_status.length > 0) count++;
    if (filters.fromDt) count++;
    if (filters.toDt) count++;
    if (filters.past_customer !== undefined && filters.past_customer !== "") count++;
    if (filters.company_id !== undefined && filters.company_id !== "") count++;
    if (filters.property_id !== undefined && filters.property_id !== "") count++;
    if (filters.employer_search !== undefined && filters.employer_search !== "") count++;
    if (filters.product_id && filters.product_id.length > 0) count++;
    if (filters.model_id && filters.model_id.length > 0) count++;
    if (filters.screening_approach && String(filters.screening_approach).trim()) count++;
    if (filters.risk_rating && filters.risk_rating.length > 0) count++;
    return count;
  }, [filters]);

  const availableColumnOptions = useMemo(() => {
    const canShowProductColumn = user
      ? hasPolicy(user, ["application_list_product_filter"])
      : false;
    const canShowModelColumn = user
      ? hasPolicy(user, ["application_list_model_filter"])
      : false;
    const canShowScreeningApproachColumn = user
      ? hasPolicy(user, ["application_list_screening_approach_filter"])
      : false;
    const canViewProgress = user ? hasPolicy(user, ["application_detail_view"]) : true;
    const canViewCreditScore = Boolean(
      user &&
      hasPolicy(user, ["application_detail_view"]) &&
      hasPolicy(user, ["application_list_credit_score_view"]),
    );
    const isLibertyRole = user?.current_role?.role_name
      ? LIBERTY_ROLES_FOR_APPLICATION_LIST.includes(user.current_role.role_name)
      : false;

    const availableColumnIds = new Set<ApplicationListColumnId>(
      APPLICATION_LIST_CUSTOMIZABLE_COLUMN_OPTIONS.filter((column) => {
        if (column.id === "product_name") return canShowProductColumn;
        if (column.id === "model_name") return canShowModelColumn;
        if (column.id === "screening_approach") return canShowScreeningApproachColumn;
        if (column.id === "userwriter_name") return isLibertyRole;
        if (column.id === "progress_status") return canViewProgress;
        if (column.id === "corelogic_applicant_score") return canViewCreditScore;
        if (column.id === "income_score") return canViewCreditScore;
        return true;
      }).map((column) => column.id),
    );

    return dropdownColumnOrderIds
      .filter((columnId) => !isApplicationListLockedColumn(columnId))
      .map((columnId) =>
        APPLICATION_LIST_COLUMN_OPTIONS.find((column) => column.id === columnId),
      )
      .filter(
        (column): column is (typeof APPLICATION_LIST_CUSTOMIZABLE_COLUMN_OPTIONS)[number] =>
          column !== undefined && availableColumnIds.has(column.id),
      );
  }, [dropdownColumnOrderIds, user]);

  const filteredColumnOptions = useMemo(() => {
    const normalizedSearch = columnSearchTerm.trim().toLowerCase();
    if (!normalizedSearch) return availableColumnOptions;

    return availableColumnOptions.filter((column) =>
      column.label.toLowerCase().includes(normalizedSearch),
    );
  }, [availableColumnOptions, columnSearchTerm]);

  const visibleColumnSet = useMemo(
    () => new Set<ApplicationListColumnId>(visibleColumnIds),
    [visibleColumnIds],
  );

  const selectedAvailableColumnCount = availableColumnOptions.filter((column) =>
    visibleColumnSet.has(column.id),
  ).length;
  const areAllAvailableColumnsSelected =
    selectedAvailableColumnCount === availableColumnOptions.length;
  const areNoAvailableColumnsSelected = selectedAvailableColumnCount === 0;

  const setColumnsInDefaultOrder = (columnIds: ApplicationListColumnId[]) => {
    setColumnSettings((currentSettings) => ({
      ...currentSettings,
      visibleColumnIds: withLockedApplicationListColumns(
        currentSettings.columnOrderIds.filter((columnId) =>
          columnIds.includes(columnId),
        ),
      ),
    }));
  };

  const toggleColumn = (columnId: ApplicationListColumnId) => {
    if (isApplicationListLockedColumn(columnId)) return

    setColumnSettings((currentSettings) => {
      const nextColumnIds = currentSettings.visibleColumnIds.includes(columnId)
        ? currentSettings.visibleColumnIds.filter(
            (currentColumnId) => currentColumnId !== columnId,
          )
        : [...currentSettings.visibleColumnIds, columnId];

      return {
        ...currentSettings,
        visibleColumnIds: withLockedApplicationListColumns(
          currentSettings.columnOrderIds.filter((orderedColumnId) =>
            nextColumnIds.includes(orderedColumnId),
          ),
        ),
      };
    });
  };

  const getReorderedColumnIds = (
    currentOrderIds: ApplicationListColumnId[],
    sourceColumnId: ApplicationListColumnId,
    targetColumnId: ApplicationListColumnId,
    placement: "before" | "after",
  ) => {
    if (
      sourceColumnId === targetColumnId ||
      isApplicationListLockedColumn(sourceColumnId) ||
      isApplicationListLockedColumn(targetColumnId)
    ) {
      return currentOrderIds;
    }

    const nextOrderIds = currentOrderIds.filter(
      (columnId) => columnId !== sourceColumnId,
    );
    const targetIndex = nextOrderIds.indexOf(targetColumnId);

    if (targetIndex === -1) return currentOrderIds;

    nextOrderIds.splice(
      placement === "after" ? targetIndex + 1 : targetIndex,
      0,
      sourceColumnId,
    );

    return withLockedApplicationListColumns(nextOrderIds);
  };

  const handleColumnDragStart = (
    event: DragEvent<HTMLButtonElement>,
    columnId: ApplicationListColumnId,
  ) => {
    setDraggedColumnId(columnId);
    setDragPreviewColumnOrderIds(columnOrderIds);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", columnId);
    const row = event.currentTarget.closest("[data-column-option-row]");
    if (row instanceof HTMLElement) {
      event.dataTransfer.setDragImage(row, 16, row.offsetHeight / 2);
    }
  };

  const autoScrollColumnsList = (clientY: number) => {
    const listElement = columnsListRef.current;
    if (!listElement) return;

    const { top, bottom } = listElement.getBoundingClientRect();
    const scrollZone = 24;
    const maxScrollStep = 2;

    if (clientY < top + scrollZone) {
      const distanceIntoZone = top + scrollZone - clientY;
      listElement.scrollBy({
        top: -Math.min(maxScrollStep, Math.max(0.5, distanceIntoZone / 16)),
      });
      return;
    }

    if (clientY > bottom - scrollZone) {
      const distanceIntoZone = clientY - (bottom - scrollZone);
      listElement.scrollBy({
        top: Math.min(maxScrollStep, Math.max(0.5, distanceIntoZone / 16)),
      });
    }
  };

  const handleColumnDragOver = (
    event: DragEvent<HTMLDivElement>,
    targetColumnId: ApplicationListColumnId,
  ) => {
    event.preventDefault();
    autoScrollColumnsList(event.clientY);

    const sourceColumnId =
      (event.dataTransfer.getData("text/plain") as ApplicationListColumnId) ||
      draggedColumnId;

    if (!sourceColumnId || sourceColumnId === targetColumnId) return;

    const targetRect = event.currentTarget.getBoundingClientRect();
    const placement =
      event.clientY > targetRect.top + targetRect.height / 2 ? "after" : "before";

    setDragPreviewColumnOrderIds((currentOrderIds) =>
      getReorderedColumnIds(
        currentOrderIds ?? columnOrderIds,
        sourceColumnId,
        targetColumnId,
        placement,
      ),
    );
  };

  const handleColumnDrop = (
    event: DragEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    if (dragPreviewColumnOrderIds) {
      setColumnSettings((currentSettings) => ({
        ...currentSettings,
        visibleColumnIds: withLockedApplicationListColumns(
          dragPreviewColumnOrderIds.filter((orderedColumnId) =>
            currentSettings.visibleColumnIds.includes(orderedColumnId),
          ),
        ),
        columnOrderIds: withLockedApplicationListColumns(dragPreviewColumnOrderIds),
      }));
    }
    setDraggedColumnId(null);
    setDragPreviewColumnOrderIds(null);
  };

  const resetColumnsToDefault = () => {
    setColumnSearchTerm("");
    setColumnSettings({
      visibleColumnIds: DEFAULT_APPLICATION_COLUMN_IDS,
      columnOrderIds: DEFAULT_APPLICATION_COLUMN_IDS,
    });
  };

  const handleView = (applicantId: number) => {
    navigate(`/application/${applicantId}`);
  };

  const requestImpersonate = (
    roleId: number,
    applicantId: number,
    isMobile?: boolean,
  ) => {
    setPendingImpersonate({ roleId, applicantId, isMobile });
    setShowImpersonateModal(true);
  };

  const performImpersonate = async () => {
    if (!pendingImpersonate) return;
    const { roleId, applicantId, isMobile } = pendingImpersonate;

    try {
      // Store admin token before impersonation (Angular parity).
      const currentToken = localStorage.getItem(STORAGE_KEYS.authToken);
      if (currentToken) {
        localStorage.setItem("admin_token", currentToken);
        localStorage.setItem("old_token", currentToken);
      }

      const returnPath = getCurrentPathForImpersonateReturn();
      localStorage.setItem("impersonate_path", returnPath);
      localStorage.setItem("impersonatepath", returnPath);

      localStorage.setItem("impersonated_applicant_id", String(applicantId));

      sessionStorage.setItem("impersonate_process", "1");

      const token = await impersonateUser(roleId);

      setAuthToken(token);
      localStorage.setItem(STORAGE_KEYS.authToken, token);

      if (isMobile) {
        window.open(getMobileImpersonationUrl(token), "_blank");
      }

      const verifyResponse = await apiClient.get(
        API_ENDPOINTS.VERIFY_USER + "?force",
      );
      if (verifyResponse.data) {
        dispatch(updateUser(verifyResponse.data));

        localStorage.setItem("impersonate", "true");
      }

      if (isMobile) {
        window.location.href = "/dashboard";
      } else {
        // Desktop: full reload to resident-application (Angular ROLE_RENTER path).
        window.location.href = `/resident-application/${applicantId}`;
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : typeof err === "object" && err !== null && "error" in err
            ? (err as { error?: string }).error
            : "Failed to impersonate user. Access denied.";
      showError(String(errorMessage), false);
    } finally {
      setPendingImpersonate(null);
    }
  };

  const closeImpersonateModal = () => {
    setShowImpersonateModal(false);
    setPendingImpersonate(null);
  };

  const handleShowDenialReasons = (applicantId: number) => {
    setSelectedApplicantId(applicantId);
    setDenialModalOpen(true);
  };

  const handleCloseDenialModal = () => {
    setDenialModalOpen(false);
    setSelectedApplicantId(null);
  };

  const totalPages = pagination.last || 1;
  const currentPage = pagination.current || 1;

  const renderPaginationBar = (placement: "header" | "footer") => (
    <div
      data-testid={`application-list-pagination-${placement}-bar`}
      className={`${placement === "header" ? "border-b" : "border-t"} border-slate-100 px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4 bg-slate-50/50`}
    >
      <div
        data-testid={`application-list-pagination-${placement}-summary`}
        className="text-sm text-slate-500 font-medium flex items-center gap-1.5"
      >
        <span>Page</span>
        {totalPages > 1 ? (
          <GotoPageInput
            currentPage={currentPage}
            totalPages={totalPages}
            onGoto={changePage}
            testId={`application-list-pagination-${placement}-goto-input`}
          />
        ) : (
          <span className="text-slate-900 font-bold">{pagination.current}</span>
        )}
        <span>
          of <span className="text-slate-900 font-bold">{pagination.last}</span>
        </span>
        <span className="mx-1 text-slate-300">|</span>
        <span>
          Total <span className="text-slate-900 font-bold">{pagination.total}</span> Records
        </span>
      </div>

      <div className="flex items-center gap-2">
        <ListPaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={changePage}
          testIdPrefix="application-list"
          placement={placement}
        />

        <div
          data-testid={`application-list-pagination-${placement}-rows-per-page`}
          className="ml-4 flex items-center gap-2"
        >
          <span
            data-testid={`application-list-pagination-${placement}-rows-per-page-label`}
            className="text-xs font-semibold text-slate-400 uppercase tracking-wider"
          >
            Rows
          </span>
          <select
            data-testid={`application-list-pagination-${placement}-rows-per-page-select`}
            className="block rounded-lg border border-slate-200 bg-white p-2 text-sm text-slate-700 focus:border-button focus:outline-none focus:ring-2 focus:ring-button/30"
            value={filters.perPage || 15}
            onChange={(e) => changePerPage(parseInt(e.target.value, 10))}
          >
            {PER_PAGE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={styles.page}
      style={{ padding: 0 }}
      data-testid="application-list-page"
    >
      <PageHeader title="APPLICATIONS" />

      <div
        className="w-full mx-auto px-3 sm:px-6 py-4 sm:py-8 flex flex-col gap-4 sm:gap-6"
        data-testid="application-list-page-content"
      >
        {/* -- Search & Actions Row -- */}
        <div
          className="flex flex-col md:flex-row items-center justify-between gap-4"
          data-testid="application-list-toolbar"
        >
          {/* Large Search Input */}
          <div
            className="relative w-full md:w-1/2 max-w-lg"
            data-testid="application-list-search-wrap"
          >
            <div
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              data-testid="application-list-search-icon"
              aria-hidden
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search applications..."
              data-testid="application-list-search-input"
              className="w-full pl-12 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder:text-slate-400"
              value={searchTerm}
              onChange={(e) =>
                setSearchDraft({
                  source: filterSearchValue,
                  value: e.target.value,
                })
              }
            />
          </div>

          {/* Action Buttons: Columns + Filter */}
          <div
            className="flex items-center gap-3 w-full md:w-auto"
            data-testid="application-list-toolbar-actions"
          >
            <div
              ref={columnsMenuRef}
              className="relative flex-1 md:flex-none"
              data-testid="application-list-columns-menu"
            >
              <button
                type="button"
                onClick={() => setIsColumnsOpen((isOpen) => !isOpen)}
                data-testid="application-list-columns-open-button"
                className={`w-full md:w-auto flex items-center justify-center gap-2 border px-5 py-2.5 rounded-xl font-semibold shadow-sm hover:shadow-md transition-all text-sm whitespace-nowrap relative ${
                  selectedAvailableColumnCount < availableColumnOptions.length
                    ? "border-[#1597ff] bg-white text-[#1597ff] hover:bg-blue-50"
                    : "border-[#1597ff] bg-white text-[#1597ff] hover:bg-blue-50"
                }`}
                aria-expanded={isColumnsOpen}
                aria-haspopup="menu"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <rect width="18" height="18" x="3" y="3" rx="2" />
                  <path d="M9 3v18" />
                  <path d="M15 3v18" />
                </svg>
                Columns
                <span
                  className={`text-xs font-bold ${
                    selectedAvailableColumnCount < availableColumnOptions.length
                      ? "text-[#1597ff]"
                      : "text-[#1597ff]"
                  }`}
                  data-testid="application-list-columns-selected-count"
                >
                  {selectedAvailableColumnCount}/{availableColumnOptions.length}
                </span>
              </button>

              {isColumnsOpen && (
                <div
                  className="absolute right-0 top-[calc(100%+0.5rem)] z-30 w-[min(24rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
                  data-testid="application-list-columns-dropdown"
                  role="menu"
                >
                  <div className="border-b border-slate-100 p-4">
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold text-slate-900">Customize columns</p>
                        <p className="mt-1 text-xs leading-5 text-slate-500">
                          Select columns and drag the handle to reorder.
                        </p>
                      </div>
                      <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-600">
                        {selectedAvailableColumnCount}/{availableColumnOptions.length}
                      </span>
                    </div>
                    <div className="relative">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                        aria-hidden
                      >
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.3-4.3" />
                      </svg>
                      <input
                        type="search"
                        value={columnSearchTerm}
                        onChange={(event) => setColumnSearchTerm(event.target.value)}
                        placeholder="Search columns..."
                        className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-50"
                        data-testid="application-list-columns-search-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-3">
                    <button
                      type="button"
                      className={`rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition ${
                        areAllAvailableColumnsSelected
                          ? "cursor-not-allowed opacity-50"
                          : "hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                      }`}
                      disabled={areAllAvailableColumnsSelected}
                      onClick={() => setColumnsInDefaultOrder(DEFAULT_CUSTOMIZABLE_COLUMN_IDS)}
                      data-testid="application-list-columns-select-all-button"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      className={`rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition ${
                        areNoAvailableColumnsSelected
                          ? "cursor-not-allowed opacity-50"
                          : "hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                      }`}
                      disabled={areNoAvailableColumnsSelected}
                      onClick={() =>
                        setColumnSettings((currentSettings) => ({
                          ...currentSettings,
                          visibleColumnIds: [...APPLICATION_LIST_LOCKED_COLUMN_IDS],
                        }))
                      }
                      data-testid="application-list-columns-clear-all-button"
                    >
                      Clear All
                    </button>
                    <button
                      type="button"
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                      onClick={resetColumnsToDefault}
                      data-testid="application-list-columns-reset-button"
                    >
                      Reset
                    </button>
                  </div>

                  <div
                    ref={columnsListRef}
                    className="max-h-72 space-y-1 overflow-y-auto p-3"
                    onDragOver={(event) => {
                      event.preventDefault();
                      autoScrollColumnsList(event.clientY);
                    }}
                    onDrop={handleColumnDrop}
                  >
                    {filteredColumnOptions.length > 0 ? (
                      filteredColumnOptions.map((column) => (
                        <div
                          key={column.id}
                          className={`group flex items-center gap-3 rounded-xl border border-transparent px-3 py-2.5 text-sm font-semibold text-slate-700 transition ${
                            draggedColumnId === column.id
                              ? "scale-[0.99] border-blue-100 bg-blue-50 opacity-80 shadow-inner"
                              : "hover:border-slate-100 hover:bg-slate-50"
                          }`}
                          data-column-option-row
                          data-testid={`application-list-columns-option-${column.id}`}
                          onDragOver={(event) => handleColumnDragOver(event, column.id)}
                          onDrop={handleColumnDrop}
                        >
                          <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                            <input
                              type="checkbox"
                              checked={visibleColumnSet.has(column.id)}
                              onChange={() => toggleColumn(column.id)}
                              className="h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="truncate leading-5">{column.label}</span>
                          </label>
                          <button
                            type="button"
                            draggable
                            className="ml-auto inline-flex h-8 w-8 shrink-0 cursor-grab items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-400 shadow-sm transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 active:cursor-grabbing group-hover:border-slate-300"
                            onDragStart={(event) => handleColumnDragStart(event, column.id)}
                            onDragEnd={() => {
                              setDraggedColumnId(null);
                              setDragPreviewColumnOrderIds(null);
                            }}
                            aria-label={`Drag ${column.label} to reorder`}
                            title="Drag to reorder"
                            data-testid={`application-list-columns-drag-handle-${column.id}`}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden
                            >
                              <circle cx="9" cy="5" r="1" />
                              <circle cx="9" cy="12" r="1" />
                              <circle cx="9" cy="19" r="1" />
                              <circle cx="15" cy="5" r="1" />
                              <circle cx="15" cy="12" r="1" />
                              <circle cx="15" cy="19" r="1" />
                            </svg>
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-8 text-center text-sm text-slate-500">
                        No columns match your search.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setIsFilterOpen(true)}
              data-testid="application-list-filters-open-button"
              className={`flex-1 md:flex-none flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-slate-700 border px-5 py-2.5 rounded-xl font-semibold shadow-sm hover:shadow-md transition-all text-sm whitespace-nowrap relative ${activeFilterCount > 0
                ? "border-blue-400 text-blue-700"
                : "border-slate-200"
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              Filters
              {activeFilterCount > 0 && (
                <span
                  data-testid="application-list-filters-active-count-badge"
                  className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-bold leading-none"
                >
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filters Drawer (Portal) */}
        <ApplicationFilters
          filters={filters}
          onFiltersChange={applyFilters}
          onReset={resetFilters}
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
        />

        {/* Table & Pagination Card */}
        <div
          className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
          data-testid="application-list-results-card"
        >
          {loading && <ApplicationListLoadingState />}

          {error && !loading && (
            <div
              className="m-4 rounded-lg bg-red-50 p-4 text-center text-red-600 sm:p-8"
              data-testid="application-list-error"
            >
              <p className="text-sm" data-testid="application-list-error-message">
                {error}
              </p>
            </div>
          )}

          {!loading && !error && (
            <>
              {applications.length > 0 && renderPaginationBar("header")}

              <div
                ref={resultsScrollRef}
                className="overflow-x-auto"
                data-testid="application-list-results-scroll"
              >
                <div className="min-w-full inline-block align-middle" data-testid="application-list-results">
                {applications.length > 0 ? (
                  isListView ? (
                    <ApplicationTable
                      applications={applications}
                      onSort={sort}
                      sortBy={filters.sortBy}
                      orderBy={filters.orderBy}
                      isProcessing={sorting}
                      visibleColumnIds={visibleColumnIds}
                      orderedColumnIds={columnOrderIds}
                      onView={handleView}
                      onImpersonate={requestImpersonate}
                      onShowDenialReasons={handleShowDenialReasons}
                      onOpenProductDetails={setSelectedProduct}
                    />
                  ) : (
                    <ApplicationGrid
                      applications={applications}
                      onView={handleView}
                      onImpersonate={requestImpersonate}
                      onShowDenialReasons={handleShowDenialReasons}
                      onOpenProductDetails={setSelectedProduct}
                    />
                  )
                ) : (
                  <div
                    className="bg-white p-12 text-center"
                    data-testid="application-list-empty-state"
                  >
                    <p
                      className="text-gray-500"
                      data-testid="application-list-empty-state-message"
                    >
                      No applications found
                    </p>
                  </div>
                )}
              </div>
            </div>

            {applications.length > 0 && renderPaginationBar("footer")}

            {applications.length > 0 && isListView && (
              <StickyTableScrollbar
                containerRef={resultsScrollRef}
                data-testid="application-list-table-sticky-scrollbar"
              />
            )}
            </>
          )}
        </div>

        {showImpersonateModal && (
          <ConfirmationModal
            title="Confirm Impersonation Request"
            message="For best results, all other Cornerstone tabs should be closed before proceeding with impersonating."
            positiveTitle="OK"
            negativeTitle="Cancel"
            testIdPrefix="application-list-confirm-impersonate"
            onConfirm={() => {
              setShowImpersonateModal(false);
              void performImpersonate();
            }}
            onCancel={closeImpersonateModal}
            onClose={closeImpersonateModal}
          />
        )}

        <DenialReasonsModal
          applicantId={selectedApplicantId}
          isOpen={denialModalOpen}
          onClose={handleCloseDenialModal}
          denialEmailContentCallSite="adminApplication"
        />
        {selectedProduct ? (
          <ProductDetailDrawer
            productName={selectedProduct.product_name}
            productDescription={getProductLongDescription(selectedProduct)}
            onClose={() => setSelectedProduct(null)}
            testId="application-list-product-panel"
            titleTestId="application-list-product-panel-title"
            closeButtonTestId="application-list-product-panel-close-button"
          />
        ) : null}
      </div>
    </div>
  );
}
